import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getTurnstileSiteKey,
  isTurnstileEnforced,
  removeTurnstileWidget,
  TURNSTILE_SCRIPT_ID,
  TURNSTILE_SCRIPT_SRC,
} from '../utils/turnstile';

export function useTurnstileWidget(containerId: string) {
  const enforced = isTurnstileEnforced();
  const widgetIdRef = useRef<string | undefined>(undefined);
  const renderWidgetRef = useRef<(() => void) | null>(null);
  const [token, setToken] = useState('');
  const [error, setError] = useState('');

  const retry = useCallback(() => {
    setToken('');
    setError('');
    if (widgetIdRef.current && window.turnstile?.reset) {
      window.turnstile.reset(widgetIdRef.current);
      return;
    }
    renderWidgetRef.current?.();
  }, []);

  const resetWidget = useCallback(() => {
    setToken('');
    if (widgetIdRef.current && window.turnstile?.reset) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, []);

  useEffect(() => {
    const siteKey = getTurnstileSiteKey();
    if (!siteKey || !enforced) {
      return undefined;
    }

    let cancelled = false;

    const renderWidget = () => {
      const container = document.getElementById(containerId);
      if (!container || !window.turnstile || cancelled) {
        return;
      }

      removeTurnstileWidget(widgetIdRef.current);
      widgetIdRef.current = undefined;
      container.innerHTML = '';

      widgetIdRef.current = window.turnstile.render(container, {
        sitekey: siteKey,
        callback: (nextToken: string) => {
          setToken(nextToken);
          setError('');
        },
        'expired-callback': () => {
          setToken('');
          setError('Captcha expired. Reload the captcha to continue.');
        },
        'error-callback': () => {
          setToken('');
          setError('Captcha failed to load. Reload the captcha to try again.');
        },
      });
    };

    renderWidgetRef.current = renderWidget;

    const existing = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;

    if (existing) {
      if (window.turnstile) {
        renderWidget();
      } else {
        existing.addEventListener('load', renderWidget, { once: true });
      }
    } else {
      const script = document.createElement('script');
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = TURNSTILE_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = renderWidget;
      document.head.appendChild(script);
    }

    return () => {
      cancelled = true;
      removeTurnstileWidget(widgetIdRef.current);
      widgetIdRef.current = undefined;
      renderWidgetRef.current = null;
    };
  }, [containerId, enforced]);

  return {
    enforced,
    token,
    error,
    setError,
    retry,
    resetWidget,
  };
}
